import { useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

/** 드로어를 앱 안에서 열었는지 표시하는 history state 키 */
const OPENED_IN_APP = 'drugDrawerOpenedInApp';

/**
 * 의약품 상세 드로어 열기 · 닫기 (M-15 D)
 * - 열기: ?drug=보험코드 를 history에 1건 push → 브라우저 뒤로가기 = 닫기
 * - 닫기: 앱 안에서 연 경우 history.back(), 딥링크로 바로 들어온 경우 drug만 지운다(replace)
 * - 교체: 드로어 안에서 다른 약으로 바꿀 때(동일성분 행 · 브레드크럼)는 replace로 바꾼다.
 *   history를 드로어당 1건만 쌓아야 브라우저 뒤로가기 = 닫기가 유지된다 (M-15 D).
 *   이전 약으로 돌아가기는 드로어 안 브레드크럼 스택이 맡는다.
 */
export const useDrugDrawer = () => {
  const navigate = useNavigate();
  const router = useRouter();

  const open = useCallback(
    (drugCode: string) =>
      navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          drug: drugCode,
        }),
        state: (prev) => ({ ...prev, [OPENED_IN_APP]: true }),
      }),
    [navigate],
  );

  const replace = useCallback(
    (drugCode: string) =>
      navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          drug: drugCode,
        }),
        state: (prev) => prev,
        replace: true,
      }),
    [navigate],
  );

  const close = useCallback(() => {
    const state = router.state.location.state as unknown as Record<
      string,
      unknown
    >;
    if (state?.[OPENED_IN_APP]) {
      router.history.back();
      return;
    }
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, drug: undefined }),
      replace: true,
    });
  }, [navigate, router]);

  return { open, replace, close };
};
